import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { setNavigate } from './lib/navigation';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AdminDashboard from './pages/AdminDashboard';
import StartupDashboard from './pages/StartupDashboard';
import InvestorDashboard from './pages/InvestorDashboard';
import InvestorNetworkDashboard from './pages/InvestorNetworkDashboard';
import EventOrganizerDashboard from './pages/EventOrganizerDashboard';
import UserDashboard from './pages/UserDashboard';
import ViewerDashboard from './pages/ViewerDashboard';
import InvestorNetworkDetail from './pages/InvestorNetworkDetail';
import InvestorDetail from './pages/InvestorDetail';
import StartupDetail from './pages/StartupDetail';
import PitchEventDetail from './pages/PitchEventDetail';
import PitchEventList from './pages/PitchEventList';
import SettingsPage from './pages/SettingsPage';
import InvestmentManagement from './pages/InvestmentManagement';
import InvestorsManagement from './pages/InvestorsManagement';
import ReportsPage from './pages/ReportsPage';
import SubscriptionPlans from './pages/SubscriptionPlans';
import MySubscription from './pages/MySubscription';
import BillingDashboard from './pages/BillingDashboard';
import PurchaseAddon from './pages/PurchaseAddon';
import AuditLogPage from './pages/AuditLogPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';

const AppRoutes = () => {
	const { user, isAuthenticated } = useAuth();
	const tenantPath = user?.tenantIdentifier || 'default';
	const navigate = useNavigate();

	// Set up navigation utility for use in interceptors
	useEffect(() => {
		setNavigate(navigate);

		// Listen for custom navigation events
		const handleNavigation = (event) => {
			navigate(event.detail.path);
		};
		window.addEventListener('spa-navigate', handleNavigation);

		return () => {
			window.removeEventListener('spa-navigate', handleNavigation);
		};
	}, [navigate]);

	return (
		<Routes>
			{/* Public routes - not under tenant */}
			<Route path="/login" element={<Login />} />
			<Route path="/signup" element={<Signup />} />
			<Route path="/privacy" element={<PrivacyPolicy />} />
			<Route path="/terms" element={<TermsOfService />} />

			{/* Tenant Routes Wrapper */}
			<Route path="/:tenantId">
				{/* Show login page at tenant root */}
				<Route index element={<Login />} />
				{/* Admin Layout Routes */}
				<Route element={<AdminLayout />}>
					<Route
						path="dashboard"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="files"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="startups"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="startups/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<StartupDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investors"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investors/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<InvestorDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investors/networks/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<InvestorNetworkDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="users"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="reports"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="events"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'EVENT_ORGANIZER']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="events/:id"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'EVENT_ORGANIZER']}>
								<PitchEventDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="account"
						element={
							<ProtectedRoute allowedRoles={["ADMIN", "TENANT_ADMIN"]}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="settings"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="investments"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<InvestmentManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="audit-logs"
						element={
							<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN']}>
								<AuditLogPage />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="startup/dashboard"
					element={
						<ProtectedRoute allowedRoles={['STARTUP']}>
							<StartupDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor/dashboard"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR']}>
							<InvestorDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor/startups/:id"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR']}>
							<StartupDetail />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor-network/dashboard"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK']}>
							<InvestorNetworkDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="investor-network/startups/:id"
					element={
						<ProtectedRoute allowedRoles={['INVESTOR_NETWORK']}>
							<StartupDetail />
						</ProtectedRoute>
					}
				/>

				{/* Subscription Routes - Available to all authenticated users */}
				<Route
					path="subscription/plans"
					element={
						<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR', 'INVESTOR', 'INVESTOR_NETWORK']}>
							<SubscriptionPlans />
						</ProtectedRoute>
					}
				/>
				<Route
					path="my-subscription"
					element={
						<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR', 'INVESTOR', 'INVESTOR_NETWORK']}>
							<MySubscription />
						</ProtectedRoute>
					}
				/>
				<Route
					path="billing"
					element={
						<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR', 'INVESTOR', 'INVESTOR_NETWORK']}>
							<BillingDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="purchase-addon"
					element={
						<ProtectedRoute allowedRoles={['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR', 'INVESTOR', 'INVESTOR_NETWORK']}>
							<PurchaseAddon />
						</ProtectedRoute>
					}
				/>

				<Route
					path="organizer/dashboard"
					element={
						<ProtectedRoute allowedRoles={['EVENT_ORGANIZER']}>
							<EventOrganizerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="user/dashboard"
					element={
						<ProtectedRoute allowedRoles={['MANAGEMENT']}>
							<UserDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/dashboard"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/files"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/startups"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/investors"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/events"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/users"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="viewer/reports"
					element={
						<ProtectedRoute allowedRoles={['VIEWER']}>
							<ViewerDashboard />
						</ProtectedRoute>
					}
				/>
			</Route>

			<Route
				path="/"
				element={
					isAuthenticated ? (
						user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN' ? (
							<Navigate to={`/${tenantPath}/dashboard`} replace />
						) : user?.role === 'STARTUP' ? (
							<Navigate to={`/${tenantPath}/startup/dashboard`} replace />
						) : user?.role === 'INVESTOR' ? (
							<Navigate to={`/${tenantPath}/investor/dashboard`} replace />
						) : user?.role === 'INVESTOR_NETWORK' ? (
							<Navigate to={`/${tenantPath}/investor-network/dashboard`} replace />
						) : user?.role === 'EVENT_ORGANIZER' ? (
							<Navigate to={`/${tenantPath}/organizer/dashboard`} replace />
						) : user?.role === 'MANAGEMENT' ? (
							<Navigate to={`/${tenantPath}/user/dashboard`} replace />
						) : user?.role === 'VIEWER' ? (
							<Navigate to={`/${tenantPath}/viewer/dashboard`} replace />
						) : (
							<Navigate to="/login" replace />
						)
					) : (
						<LandingPage />
					)
				}
			/>
		</Routes>
	);
};

const App = () => {
	return (
		<AuthProvider>
			<div className="min-h-screen bg-gray-50">
				<AppRoutes />
			</div>
		</AuthProvider>
	);
};

export default App;


